import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import AxiosApiCall from "../api/AxiosApiCall";
import API_URLS from "../api/ApiServices";
import { toast } from "react-toastify";

const axiosInstance = AxiosApiCall();


// Fetch all head offices
export const useHeadOffices = (filters = {}) => {
  return useQuery({
    queryKey: ["headOffices", filters],
    queryFn: async () => {
      try {
        const params = new URLSearchParams();
        if (filters.start_date) params.append("start_date", filters.start_date);
        if (filters.end_date) params.append("end_date", filters.end_date);

        const url = `${API_URLS.getAllHeadOffices}${params.toString() ? `?${params.toString()}` : ""}`;
        const response = await axiosInstance.get(url);
        // console.log("Head Offices API Response:", response.data);
        return response.data;
      } catch (error) {
        // console.error("Error fetching head offices:", error);
        toast.error(error?.response?.data?.message || "Failed to fetch head offices");
        throw error;
      }
    },
  });
};

// Fetch head office by ID
export const useHeadOfficeById = (headOfficeId) => {
  return useQuery({
    queryKey: ["headOffice", headOfficeId],
    queryFn: async () => {
      const response = await axiosInstance.get(`${API_URLS.getHeadOfficeById}?head_office_id=${headOfficeId}`);
      return response.data;
    },
    enabled: !!headOfficeId,
  });
};

// Fetch head office dashboard
export const useHeadOfficeDashboard = (headOfficeId, filters = {}) => {
  return useQuery({
    queryKey: ["headOfficeDashboard", headOfficeId, filters],
    queryFn: async () => {
      const params = new URLSearchParams({ head_office_id: headOfficeId });
      if (filters.start_date) params.append("start_date", filters.start_date);
      if (filters.end_date) params.append("end_date", filters.end_date);

      const response = await axiosInstance.get(`${API_URLS.getHeadOfficeDashboard}?${params.toString()}`);
      return response.data;
    },
    enabled: !!headOfficeId,
  });
};

// Fetch head office inventory
export const useHeadOfficeInventory = (headOfficeId, perPage = 20) => {
  return useQuery({
    queryKey: ["headOfficeInventory", headOfficeId, perPage],
    queryFn: async () => {
      const response = await axiosInstance.get(
        `${API_URLS.getHeadOfficeInventory}?head_office_id=${headOfficeId}&per_page=${perPage}`
      );
      return response.data;
    },
    enabled: !!headOfficeId,
  });
};

// Fetch head office branches
export const useHeadOfficeBranches = (headOfficeId) => {
  return useQuery({
    queryKey: ["headOfficeBranches", headOfficeId],
    queryFn: async () => {
      const response = await axiosInstance.get(
        `${API_URLS.getHeadOfficeBranches}?head_office_id=${headOfficeId}`
      );
      return response.data;
    },
    enabled: !!headOfficeId,
  });
};

// Create head office
export const useCreateHeadOffice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await axiosInstance.post(API_URLS.createHeadOffice, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["headOffices"] });
      toast.success("Head office created successfully!");
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "Failed to create head office");
    },
  });
};

// Update head office
export const useUpdateHeadOffice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await axiosInstance.put(API_URLS.updateHeadOffice, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["headOffices"] });
      queryClient.invalidateQueries({ queryKey: ["headOffice", variables.head_office_id] });
      toast.success("Head office updated successfully!");
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "Failed to update head office");
    },
  });
};

// Delete head office
export const useDeleteHeadOffice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (headOfficeId) => {
      const response = await axiosInstance.delete(API_URLS.deleteHeadOffice, {
        data: { head_office_id: headOfficeId },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["headOffices"] });
      toast.success("Head office deleted successfully!");
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "Failed to delete head office");
    },
  });
};

// Toggle head office activation
export const useToggleHeadOfficeActivation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (headOfficeId) => {
      const response = await axiosInstance.patch(API_URLS.toggleHeadOfficeActivation, {
        head_office_id: headOfficeId,
      });
      return response.data;
    },
    onSuccess: (_, headOfficeId) => {
      queryClient.invalidateQueries({ queryKey: ["headOffices"] });
      queryClient.invalidateQueries({ queryKey: ["headOffice", headOfficeId] });
      toast.success("Head office activation status updated!");
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "Failed to update activation status");
    },
  });
};

// Toggle head office status (active/inactive/suspended)
export const useToggleHeadOfficeStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (headOfficeId) => {
      const response = await axiosInstance.patch(API_URLS.toggleHeadOfficeStatus, {
        head_office_id: headOfficeId,
      });
      return response.data;
    },
    onSuccess: (_, headOfficeId) => {
      queryClient.invalidateQueries({ queryKey: ["headOffices"] });
      queryClient.invalidateQueries({ queryKey: ["headOffice", headOfficeId] });
      toast.success("Head office status updated!");
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "Failed to update status");
    },
  });
};

// Reset head office password
export const useResetHeadOfficePassword = () => {
  return useMutation({
    mutationFn: async (headOfficeId) => {
      const response = await axiosInstance.post(API_URLS.resetHeadOfficePassword, {
        head_office_id: headOfficeId,
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success("Password reset successfully! New password sent to email.");
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "Failed to reset password");
    },
  });
};

// Fetch branch product units for allocation
export const useBranchProductUnits = (perPage = 1000) => {
  return useQuery({
    queryKey: ["branchProductUnits", perPage],
    queryFn: async () => {
      const response = await axiosInstance.post(
        `${API_URLS.getAllBranchProductUnit}`,
        { per_page: perPage }
      );
      return response.data;
    },
  });
};

// Allocate stock to head office
export const useAllocateStock = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await axiosInstance.post(API_URLS.allocateStockToHeadOffice, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["headOfficeInventory", variables.head_office_id] });
      queryClient.invalidateQueries({ queryKey: ["headOfficeDashboard", variables.head_office_id] });
      toast.success("Stock allocated successfully!");
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "Failed to allocate stock");
    },
  });
};
